import {_RequestEnvelope} from '../core/types';
import {KycStatus} from '../models/kyc';
import {AdminUser, AdminWallet} from '../models/user';

// ---------------------------------------------------------------------------
// GetKycStatus
// ---------------------------------------------------------------------------

export interface GetKycStatusRequest {
    UserId: string;
    RealmId: string | null;
}

export const GetKycStatusResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NoStatusYet: 'NoStatusYet',
    UnknownUser: 'UnknownUser',
} as const;
export type GetKycStatusResult =
    typeof GetKycStatusResultCode[keyof typeof GetKycStatusResultCode];

export type GetKycStatusResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    KycStatus: KycStatus;
}
    | {
    Result: 'Unknown' | 'NoStatusYet' | 'UnknownUser';
    ResultDescription: string;
    KycStatus: null;
};

const _getKycStatusMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NoStatusYet',
    3: 'UnknownUser',
};

export function getKycStatus(
    params: GetKycStatusRequest
): _RequestEnvelope<GetKycStatusResponse> {
    return {...params, _endpoint: 'admin/user/getKycStatus', _resultCodeMap: _getKycStatusMap};
}

// ---------------------------------------------------------------------------
// GetUser
// ---------------------------------------------------------------------------

export interface GetUserRequest {
    UserId: string;
    RealmId: string | null;
}

export const GetUserResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type GetUserResult =
    typeof GetUserResultCode[keyof typeof GetUserResultCode];

export type GetUserResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    User: AdminUser;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    User: null;
};

const _getUserMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function getUser(
    params: GetUserRequest
): _RequestEnvelope<GetUserResponse> {
    return {...params, _endpoint: 'admin/user/getUser', _resultCodeMap: _getUserMap};
}

// ---------------------------------------------------------------------------
// UpdateKycStatus
// ---------------------------------------------------------------------------

export interface UpdateKycStatusRequest {
    UserId: string;
    RealmId: string;
    NewStatus: KycStatus;
    Reason: string;
}

export const UpdateKycStatusResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type UpdateKycStatusResult =
    typeof UpdateKycStatusResultCode[keyof typeof UpdateKycStatusResultCode];

export type UpdateKycStatusResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    KycStatus: KycStatus;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    KycStatus: null;
};

const _updateKycStatusMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function updateKycStatus(
    params: UpdateKycStatusRequest
): _RequestEnvelope<UpdateKycStatusResponse> {
    return {...params, _endpoint: 'admin/user/updateKycStatus', _resultCodeMap: _updateKycStatusMap};
}

// ---------------------------------------------------------------------------
// UpdateWallet
// ---------------------------------------------------------------------------

export interface UpdateWalletRequest {
    UserId: string;
    RealmId: string;
    FreeCredits: number;
    PaidCredits: number;
    Reason: string;
    IdempotentId: string;
}

export const UpdateWalletResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    InsufficientFunds: 'InsufficientFunds',
} as const;
export type UpdateWalletResult =
    typeof UpdateWalletResultCode[keyof typeof UpdateWalletResultCode];

export type UpdateWalletResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    WasAlreadyCompleted: boolean;
    Wallet: AdminWallet;
}
    | {
    Result: 'Unknown' | 'InsufficientFunds';
    ResultDescription: string;
    WasAlreadyCompleted: boolean;
    Wallet: null;
};

const _updateWalletMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'InsufficientFunds',
};

export function updateWallet(
    params: UpdateWalletRequest
): _RequestEnvelope<UpdateWalletResponse> {
    return {...params, _endpoint: 'admin/user/updateWallet', _resultCodeMap: _updateWalletMap};
}
